import { Component, Host, Prop, State, h } from '@stencil/core';

import { AutoCompleteSuggestion, KEYBOARD_ACTIONS, handleArrowKeys } from '../../../../utils/utils.input-textbox-autocomplete';

@Component({
  tag: 'input-textbox-autocomplete',
  styleUrl: 'input-textbox-autocomplete.css',
})
export class InputTextBoxAutocomplete {
  @Prop() placeholder: string = '';
  @Prop() errorMessage: string = '';
  @Prop() hasError: boolean = false;
  @Prop() suggestions: AutoCompleteSuggestion[] = [];
  @Prop({ mutable: true }) value: string = '';
  @Prop() whenChanged: (value: string | CustomEvent) => void = () => {};
  @Prop() whenChosen: (value: string | number) => void = () => {};
  @Prop({ mutable: true }) focused: boolean = false;
  @Prop() errorHiddenMode: boolean = false;
  @Prop() prefixError: 'banner' | 'dynamic-entry' = 'banner';
  @Prop() testId: string = '';
  @Prop() isDisabled: boolean = false;

  @State() focusedIndex: number = -1;
  @State() originalValue: string = '';

  @Prop() whenPressEnter: () => void = () => {};

  onSuggestionClick = (suggestion: string | number) => {
    this.whenChosen(suggestion);
  };

  handleInputChange(value: CustomEvent<string>) {
    this.originalValue = value.detail;
    this.whenChanged(value);
    this.focused = true;
    this.focusedIndex = -1;
  }
  handleInputOnblur() {
    this.focused = false;
  }

  renderErrorMsg() {
    if (this.prefixError === 'banner') {
      return <banner-error class="flex mt-1" hasError={this.hasError} errorMessage={this.errorMessage} hiddenMode={this.errorHiddenMode} />;
    }
    return !this.hasError ? null : <dynamic-entry-error hasError={this.hasError} errorMessage={this.errorMessage} hiddenMode={this.errorHiddenMode} />;
  }

  handleArrowKeysAndEnter(event: KeyboardEvent) {
    switch (event.key) {
      case KEYBOARD_ACTIONS.ENTER:
        const selectedOption = this.suggestions[this.focusedIndex];
        this.onSuggestionClick(selectedOption.value);
        this.whenPressEnter();
        break;
      case KEYBOARD_ACTIONS.ARROW_UP:
        this.focusedIndex -= 1;
        const [valueUp, focusedIndexUp] = handleArrowKeys(KEYBOARD_ACTIONS.ARROW_UP, this.focusedIndex, this.value, this.originalValue, this.suggestions);
        this.value = valueUp?.toString();
        this.focusedIndex = parseInt(focusedIndexUp?.toString());
        break;
      case KEYBOARD_ACTIONS.ARROW_DOWN:
        this.focusedIndex += 1;
        const [value, focusedIndex] = handleArrowKeys(KEYBOARD_ACTIONS.ARROW_DOWN, this.focusedIndex, this.value, this.originalValue, this.suggestions);
        this.value = value?.toString();
        this.focusedIndex = parseInt(focusedIndex?.toString());
      default:
        break;
    }
  }

  render() {
    const dropdownClasses = {
      visible: this.suggestions.length > 0,
      hidden: this.suggestions.length <= 0 || !this.focused,
      '-top-6 z-50 absolute text-black bg-white w-fill-available': true,
      'border border-solid border-t-0 border-clrs-argo-blue': this.focused,
      'mt-4': this.focused && this.prefixError === 'dynamic-entry',
    };

    const getOptionClasses = (index: number) => ({
      'cursor-pointer p-1 pl-4 m-0 font-extrabold text-sm leading-5 text-blue-600 hover:text-white': true,
      'hover:bg-blue-400': this.focusedIndex > -1,
      'hover:bg-blue-500': this.focusedIndex === -1,
      'text-white bg-blue-500': index === this.focusedIndex,
    });

    return (
      <Host>
        <input-text-box
          {...(this.testId ? { id: this.testId } : {})}
          testId={this.testId}
          label={this.placeholder}
          value={this.value}
          hasError={this.hasError}
          onValueChange={(event) => this.handleInputChange(event)}
          onBlur={() => this.handleInputOnblur()}
          class="mb-1"
          onKeyDown={(event: KeyboardEvent) => this.handleArrowKeysAndEnter(event)}
          isDisabled={this.isDisabled}
        />
        {this.renderErrorMsg()}

        <div class="relative">
          <div class={dropdownClasses}>
            <ol class="list-none p-0 m-0">
              {this.suggestions.map((suggestion, index) => (
                <li
                  class={getOptionClasses(index)}
                  onMouseDown={(event: MouseEvent) => {
                    event.stopPropagation();
                    this.onSuggestionClick(suggestion.value);
                  }}>
                  {suggestion.label}
                </li>
              ))}
            </ol>
          </div>
        </div>
      </Host>
    );
  }
}
